"use client"

import { useState } from "react"
import { ArrowLeft, Eye, Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { SystemInstructions } from "@/components/system-instructions"
import { Configurations } from "@/components/configurations"
import { EvalsAnalysis } from "@/components/evals-analysis"
import { CallSimulation } from "@/components/call-simulation"
import { SimulationAnalytics } from "@/components/simulation-analytics"
import { CustomInstructions } from "@/components/custom-instructions"
import { PreviewModal } from "@/components/preview-modal"

interface Agent {
  id: string
  name: string
  createdDate: string
  createdBy: string
  updatedDate: string
}

interface AgentDetailProps {
  agent: Agent
  onClose: () => void
}

export function AgentDetail({ agent, onClose }: AgentDetailProps) {
  const [activeTab, setActiveTab] = useState("system-instructions")
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold">{agent.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Created by {agent.createdBy} on {agent.createdDate}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsPreviewOpen(true)} className="gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            <Button className="gap-2">
              <Upload className="h-4 w-4" />
              Publish
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="border-b border-border px-6">
          <TabsList className="h-12 bg-transparent p-0 gap-6">
            <TabsTrigger
              value="system-instructions"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              System Instructions
            </TabsTrigger>
            <TabsTrigger
              value="configurations"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              Configurations
            </TabsTrigger>
            <TabsTrigger
              value="evals"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              Evals
            </TabsTrigger>
            <TabsTrigger
              value="simulation"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              Simulation
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              Analytics
            </TabsTrigger>
            <TabsTrigger
              value="instructions"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              Instructions
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-auto">
          <TabsContent value="system-instructions" className="h-full m-0">
            <div className="p-6">
              <SystemInstructions />
            </div>
          </TabsContent>
          <TabsContent value="configurations" className="h-full m-0">
            <div className="p-6">
              <Configurations />
            </div>
          </TabsContent>
          <TabsContent value="evals" className="h-full m-0">
            <EvalsAnalysis />
          </TabsContent>
          <TabsContent value="simulation" className="h-full m-0">
            <CallSimulation />
          </TabsContent>
          <TabsContent value="analytics" className="h-full m-0">
            <SimulationAnalytics />
          </TabsContent>
          <TabsContent value="instructions" className="h-full m-0">
            <div className="p-6">
              <CustomInstructions />
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Preview Modal */}
      <PreviewModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} agentName={agent.name} />
    </div>
  )
}
